// src/services/queries/mediaInsights/twitterQueries.ts

import type { QueryFunctionContext } from "@tanstack/react-query";
import { queryKeys } from "@/constants/queryKeys";
import type {
  FetchTweetsParams,
  FetchTweetsResponse,
  Tweet,
  TwitterFiltersResponse,
} from "@/types/mediaInsights/twitter";
import { TIME } from "@/constants/app";
import { API_BASE_URL } from "@/services";

/**
 * Helper function to build the request body from params
 */
const buildRequestBody = (params?: FetchTweetsParams): FetchTweetsParams => {
  const body: FetchTweetsParams = {};

  if (params?.author_usernames && params.author_usernames.length > 0) {
    body.author_usernames = params.author_usernames;
  }

  if (params?.tickers && params.tickers.length > 0) {
    body.tickers = params.tickers;
  }

  if (params?.start_datetime) {
    body.start_datetime = params.start_datetime;
  }

  if (params?.end_datetime) {
    body.end_datetime = params.end_datetime;
  }

  if (params?.limit !== undefined) {
    body.limit = params.limit;
  }

  if (params?.offset !== undefined) {
    body.offset = params.offset;
  }

  if (params?.min_alpha_grade !== undefined) {
    body.min_alpha_grade = params.min_alpha_grade;
  }

  if (params?.max_alpha_grade !== undefined) {
    body.max_alpha_grade = params.max_alpha_grade;
  }

  if (params?.sort_by) {
    body.sort_by = params.sort_by;
  }

  if (params?.sort_order) {
    body.sort_order = params.sort_order;
  }

  return body;
};

/**
 * Query function for fetching tweets data with optional filters
 */
export const fetchTweets = async ({
  signal,
  queryKey,
}: QueryFunctionContext<
  ReturnType<typeof queryKeys.mediaInsights.tweets>
>): Promise<Tweet[]> => {
  const [, , params] = queryKey;
  const body = buildRequestBody(params as FetchTweetsParams);

  const init: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await fetch(
    `${API_BASE_URL}/multimediaInsightPage/getTweets`,
    init
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result: FetchTweetsResponse = await response.json();
  return result.data;
};

/**
 * Query function for fetching a single page of tweets (used in infinite queries)
 */
export const fetchTweetsPage = async (
  params: FetchTweetsParams,
  signal?: AbortSignal
): Promise<FetchTweetsResponse> => {
  const body = buildRequestBody(params);

  const init: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await fetch(
    `${API_BASE_URL}/multimediaInsightPage/getTweets`,
    init
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result: FetchTweetsResponse = await response.json();
  return result;
};

/**
 * Query options factory for tweets data
 */
export const tweetsQueryOptions = (params?: FetchTweetsParams) => ({
  queryKey: queryKeys.mediaInsights.tweets(params),
  queryFn: fetchTweets,
  staleTime: 5 * TIME.MINUTE, // 5 minutes - data considered fresh
  gcTime: 30 * TIME.MINUTE, // 30 minutes - garbage collection time
  refetchOnWindowFocus: false, // Don't refetch on window focus
  retry: (failureCount: number, error: Error) => {
    // Don't retry on 4xx errors
    if (error.message.includes("4")) {
      return false;
    }
    return failureCount < 3;
  },
});

/**
 * Infinite query options factory for tweets data with pagination
 */
export const tweetsInfiniteQueryOptions = (params?: Omit<FetchTweetsParams, 'offset'>, pageSize: number = 50) => ({
  queryKey: queryKeys.mediaInsights.tweets({
    ...(params as FetchTweetsParams),
    _paginated: true,
  } as FetchTweetsParams & { _paginated: boolean }),
  queryFn: ({ pageParam = 0, signal }: { pageParam: number; signal?: AbortSignal }) =>
    fetchTweetsPage(
      {
        ...params,
        offset: pageParam,
        limit: pageSize,
      },
      signal
    ),
  getNextPageParam: (lastPage: FetchTweetsResponse, allPages: FetchTweetsResponse[]) => {
    // If the last page has fewer tweets than pageSize, we've reached the end
    if (lastPage.data.length < pageSize) {
      return undefined;
    }
    // Calculate the next offset
    const currentOffset = allPages.reduce(
      (sum, page) => sum + page.data.length,
      0
    );
    return currentOffset;
  },
  initialPageParam: 0,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 30 * TIME.MINUTE,
  refetchOnWindowFocus: false,
  retry: (failureCount: number, error: Error) => {
    if (error.message.includes("4")) {
      return false;
    }
    return failureCount < 3;
  },
});

/**
 * Query function for fetching available Twitter filters (tickers and authors)
 */
export const fetchTwitterFilters = async ({
  signal,
}: QueryFunctionContext<
  ReturnType<typeof queryKeys.mediaInsights.twitterFilters>
>): Promise<TwitterFiltersResponse> => {
  const init: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await fetch(
    `${API_BASE_URL}/multimediaInsightPage/getTwitterFilters`,
    init
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result: TwitterFiltersResponse = await response.json();
  return result;
};

/**
 * Query options factory for Twitter filters data
 */
export const twitterFiltersQueryOptions = () => ({
  queryKey: queryKeys.mediaInsights.twitterFilters(),
  queryFn: fetchTwitterFilters,
  staleTime: 10 * TIME.MINUTE, // 10 minutes - filters don't change often
  gcTime: 60 * TIME.MINUTE, // 1 hour - keep in cache longer
  refetchOnWindowFocus: false, // Don't refetch on window focus
  retry: (failureCount: number, error: Error) => {
    // Don't retry on 4xx errors
    if (error.message.includes("4")) {
      return false;
    }
    return failureCount < 3;
  },
});
